// (c) 2026 Owl Consultations. All rights reserved.
// D-WB-3: pharmacy-admin self-service for custom fields + dropdown lists with colors.
// Writes only pharmacy_custom_field_defs (control-plane, pharmacy-scoped) — no tenant DB.
import { useEffect, useState, useCallback } from 'react'
import { Plus, Trash2, GripVertical } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { WorkBenchPill } from '../../components/workflow/WorkBench'

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'dropdown', label: 'Dropdown (choice list)' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'checkbox', label: 'Checkbox' },
]

interface OptionRow { label: string; color?: string | null; text?: string | null; is_default?: boolean }

function parseOptions(json: string | null): OptionRow[] {
  if (!json) return []
  try {
    const arr = JSON.parse(json)
    if (!Array.isArray(arr)) return []
    return arr.map((o: any) => typeof o === 'string' ? { label: o, color: null, text: null, is_default: false } : { label: o.label ?? '', color: o.color ?? null, text: o.text ?? null, is_default: o.is_default === true })
  } catch { return [] }
}

// ASP-79: workbench field defs, so every configurable field is visible in ONE screen.
interface WbStatusValue { value: string; sort_order: number; color_bg: string | null; color_text: string | null; retired?: boolean; is_default?: boolean }
interface WbFieldDef { field_key: string; display_name: string; internal_name: string | null; field_kind: string; sort_order: number; values: WbStatusValue[] }

export default function CustomFieldsTab() {
  const [defs, setDefs] = useState<api.CustomFieldDef[]>([])
  const [wbDefs, setWbDefs] = useState<WbFieldDef[]>([])
  const [wbEditing, setWbEditing] = useState<WbFieldDef | null>(null)
  const [backfills, setBackfills] = useState<Awaited<ReturnType<typeof api.listCustomFieldBackfills>>['items']>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [editing, setEditing] = useState<api.CustomFieldDef | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setErr(null)
    try { setDefs((await api.listCustomFieldDefs()).items) }
    catch (e: any) { setErr(e?.message || 'Failed to load custom fields') }
    finally { setLoading(false) }
    // ASP-79: workbench fields alongside custom fields. Read-only here by design —
    // they are platform-defined columns with their own value sets; this screen shows
    // what they are and what choices they carry so the whole field surface is in one
    // place. A failure here must not blank the custom-field list.
    try {
      const r = await api.apiFetch('/workbench/field-defs', { credentials: 'include' })
      if (r.ok) { const j = await r.json(); setWbDefs(j.fields ?? j.field_defs ?? j.items ?? []) }
    } catch { /* workbench section simply stays empty */ }
    try { setBackfills((await api.listCustomFieldBackfills()).items) } catch { /* history optional */ }
  }, [])
  useEffect(() => { load() }, [load])

  const remove = async (id: string) => {
    if (!confirm('Deactivate this field? Existing values are preserved.')) return
    try { await api.deleteCustomFieldDef(id); load() }
    catch (e: any) { setErr(e?.message || 'Delete failed') }
  }

  if (editing) return <FieldEditor def={editing === 'new' ? null : editing} onDone={() => { setEditing(null); load() }} onCancel={() => setEditing(null)} />
  if (wbEditing) return <WorkbenchFieldEditor def={wbEditing} onDone={() => { setWbEditing(null); load() }} onCancel={() => setWbEditing(null)} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Custom fields</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-soft)' }}>
            Create your own fields and choice lists — like SharePoint columns. Options can carry colors, and you can star one as the default value.
          </p>
        </div>
        <button onClick={() => setEditing('new')} style={primaryBtn}><Plus size={15} /> New field</button>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {loading ? <div style={{ padding: 20, color: 'var(--color-text-soft)' }}>Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {defs.filter(d => d.is_active).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-soft)' }}>No custom fields yet.</div>}
          {defs.filter(d => d.is_active).map(d => {
            const opts = parseOptions(d.dropdown_options)
            return (
              <div key={d.id} style={row}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{d.field_name}
                    <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-soft)', marginLeft: 8 }}>{d.field_type}{d.is_required ? ' · required' : ''}</span>
                  </div>
                  {d.field_type === 'dropdown' && opts.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {opts.map((o, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <WorkBenchPill value={o.label} bg={o.color} fg={o.text} />
                          {o.is_default && <span title="Default value" style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-primary, #0d9488)' }}>★</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {opts.some(o => o.is_default) && (
                  <button
                    onClick={async () => {
                      try {
                        const p = await api.previewCustomFieldBackfill(d.id)
                        if (!p.has_default) { alert(p.message); return }
                        if (p.rows_matched === 0) { alert('Every prescription already has a value for this field — nothing to fill.'); return }
                        // Dry-run shown; explicit confirm before any write (D-CFD-1: no silent mass write).
                        if (!confirm(`${p.message}\n\nApply now? This is reversible from Backfill history.`)) return
                        const r = await api.applyCustomFieldBackfill(d.id, p.batch_id)
                        alert(r.message)
                        load()
                      } catch (e: any) { setErr(e?.message || 'Backfill failed') }
                    }}
                    title="Fill prescriptions that have no value with this default (reversible)"
                    style={linkBtn}
                  >Apply default to existing</button>
                )}
                <button onClick={() => setEditing(d)} style={linkBtn}>Edit</button>
                <button onClick={() => remove(d.id)} style={{ ...linkBtn, color: '#b91c1c' }}><Trash2 size={14} /></button>
              </div>
            )
          })}
        </div>
      )}

      {/* D-CFD-1 — backfill history with undo */}
      {backfills.filter(b => b.status === 'applied' || b.status === 'undone').length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Default backfills</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-soft)' }}>
            Each time you apply a default to existing prescriptions it is recorded here and can be undone.
            Undo only clears rows this batch filled and that nobody has changed since.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {backfills.filter(b => b.status === 'applied' || b.status === 'undone').map(b => (
              <div key={b.batch_id} style={row}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{b.value}
                    <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-soft)', marginLeft: 8 }}>
                      {b.rows_written.toLocaleString()} filled · {new Date(b.created_at).toLocaleDateString()}
                      {b.status === 'undone' ? ' · undone' : ''}
                    </span>
                  </div>
                </div>
                {b.status === 'applied' && (
                  <button
                    onClick={async () => {
                      const isWb = (b as any).field_source === 'workbench'
                      const msg = isWb
                        ? `Undo this backfill? Clears every prescription currently holding "${b.value}" in this field — including any that were manually set to the same value after the backfill (workbench rows are not batch-tagged).`
                        : `Undo this backfill? Clears the ${b.rows_written.toLocaleString()} rows it filled with "${b.value}", except any a person changed since.`
                      if (!confirm(msg)) return
                      try {
                        const r = isWb
                          ? await api.undoWorkbenchBackfill(b.batch_id)
                          : await api.undoCustomFieldBackfill(b.batch_id)
                        alert(r.message); load()
                      }
                      catch (e: any) { setErr(e?.message || 'Undo failed') }
                    }}
                    style={{ ...linkBtn, color: '#b91c1c' }}
                  >Undo</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ASP-79 — workbench fields, same screen */}
      {wbDefs.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>WorkBench fields</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-soft)' }}>
            Platform fields shown on the Rx workbench, with the choices each one offers. These
            drive the smart value pickers in the view rule builder. Edit a field to rename it or
            change its choice list — removing a choice retires it, so prescriptions already
            holding that value keep displaying it.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {wbDefs.slice().sort((a, b) => a.sort_order - b.sort_order).map(f => {
              const vals = (f.values ?? []).filter(v => !v.retired)
              return (
                <div key={f.field_key} style={row}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{f.display_name}
                      <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-soft)', marginLeft: 8 }}>
                        {f.field_kind}{f.internal_name ? ` · ${f.internal_name}` : ''} · workbench
                      </span>
                    </div>
                    {vals.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        {vals.map((v, i) => <WorkBenchPill key={i} value={v.value} bg={v.color_bg} fg={v.color_text} />)}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setWbEditing(f)} style={linkBtn}>Edit</button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ASP-80 — edit a WorkBench field: rename, reorder, and manage its choice list.
// Removing a choice RETIRES it server-side (prescriptions already holding that value
// keep displaying it); it simply stops being offered.
function WorkbenchFieldEditor({ def, onDone, onCancel }: { def: WbFieldDef; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(def.display_name)
  const [sortOrder, setSortOrder] = useState(def.sort_order)
  const [vals, setVals] = useState<WbStatusValue[]>(
    (def.values ?? []).filter(v => !v.retired).slice().sort((a, b) => a.sort_order - b.sort_order)
      .map(v => ({ ...v, is_default: v.is_default === true })))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const isStatus = def.field_kind === 'status'

  const setVal = (i: number, patch: Partial<WbStatusValue>) =>
    setVals(vs => vs.map((v, idx) => idx === i ? { ...v, ...patch } : v))
  // Exactly one default; clicking the current default clears it.
  // D-CV-4: dropdown ↔ checkbox switch. Downgrade goes through the guided
  // priority collapse (Patrick ruling 1): rank options, highest-priority selection kept.
  const [mode, setMode] = useState<'single' | 'multi'>(((def as any).selection_mode === 'multi') ? 'multi' : 'single')
  const [modeSaving, setModeSaving] = useState(false)
  async function switchMode(next: 'single' | 'multi') {
    if (next === mode) return
    setModeSaving(true)
    try {
      await api.putWorkbenchSelectionMode(def.field_key, next)
      setMode(next)
      alert(next === 'multi'
        ? 'This field is now a checkbox list — users can select more than one option. Existing values stay as-is (each reads as one checked option).'
        : 'This field is now a dropdown (single select).')
    } catch (e: any) {
      const msg = e?.message || ''
      if (msg.includes('multi_values_present') || msg.includes('multiple selections')) {
        // Guided collapse: rank the options by clicking them in priority order.
        const opts = vals.filter(v => v.value.trim()).map(v => v.value)
        const ranked: string[] = []
        let remaining = [...opts]
        while (remaining.length > 1) {
          const pick = prompt(
            `Some prescriptions hold multiple selections.\nRank the options — highest priority is KEPT, others removed.\n\nPick #${ranked.length + 1}:\n` +
            remaining.map((o, i) => `${i + 1}. ${o}`).join('\n') + `\n\nEnter a number (or Cancel to abort):`)
          if (pick === null) { setModeSaving(false); return }
          const idx = parseInt(pick, 10) - 1
          if (isNaN(idx) || idx < 0 || idx >= remaining.length) continue
          ranked.push(remaining[idx]); remaining = remaining.filter((_, i) => i !== idx)
        }
        ranked.push(...remaining)
        try {
          const r = await api.collapseWorkbenchMulti(def.field_key, ranked)
          alert(r.message)
          await api.putWorkbenchSelectionMode(def.field_key, 'single')
          setMode('single')
        } catch (e2: any) { setErr(e2?.message || 'Collapse failed') }
      } else setErr(msg || 'Mode switch failed')
    } finally { setModeSaving(false) }
  }
  const setWbDefault = (i: number) =>
    setVals(vs => vs.map((v, idx) => ({ ...v, is_default: idx === i ? !v.is_default : false })))

  // The choice list as the server should hold it. Extracted because BOTH Save and "Apply default
  // to existing" have to write it, and the two must not drift: the star on screen is the whole
  // input to the backfill, and a second hand-rolled copy of this mapping is how they stop agreeing.
  const persistValues = () => api.putWorkbenchFieldValues(def.field_key,
    vals.filter(v => v.value.trim()).map((v, i) => ({
      value: v.value.trim(), sort_order: i, color_bg: v.color_bg, color_text: v.color_text, is_default: v.is_default === true,
    })))

  const save = async () => {
    if (!name.trim()) { setErr('Display name is required.'); return }
    setSaving(true); setErr(null)
    try {
      await api.putWorkbenchFieldDef(def.field_key, { display_name: name.trim(), field_kind: def.field_kind, sort_order: sortOrder })
      if (isStatus) await persistValues()
      onDone()
    } catch (e: any) { setErr(e?.message || 'Save failed') }
    finally { setSaving(false) }
  }

  return (
    <div>
      <h2 style={{ margin: 0, fontSize: 20 }}>Edit workbench field</h2>
      <p style={{ margin: '4px 0 14px', fontSize: 13, color: 'var(--color-text-soft)' }}>
        <code>{def.field_key}</code> · {def.field_kind}. The field key is the stored column and
        cannot change — renaming only changes what staff see.
      </p>
      {err && <div style={errBox}>{err}</div>}

      <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Display name</label>
      <input value={name} onChange={e => setName(e.target.value)}
        style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, width: 320, marginBottom: 12 }} />

      <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Sort order</label>
      <input type="number" value={sortOrder} onChange={e => setSortOrder(parseInt(e.target.value) || 0)}
        style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, width: 120, marginBottom: 16 }} />

      {isStatus && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Choices ({vals.length})</label>
            <button onClick={() => setVals(vs => [...vs, { value: '', sort_order: vs.length, color_bg: null, color_text: null }])}
              style={linkBtn}><Plus size={14} /> Add choice</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {vals.length === 0 && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No choices yet.</div>}
            {vals.map((v, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <GripVertical size={14} style={{ color: 'var(--color-text-mute)' }} />
                <input value={v.value} onChange={e => setVal(i, { value: e.target.value })} placeholder="Choice label"
                  style={{ padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, flex: 1 }} />
                <input type="color" value={v.color_bg || '#f3f4f6'} onChange={e => setVal(i, { color_bg: e.target.value })}
                  title="Background color" style={{ width: 34, height: 30, padding: 0, border: '1px solid var(--color-border)', borderRadius: 6 }} />
                <input type="color" value={v.color_text || '#374151'} onChange={e => setVal(i, { color_text: e.target.value })}
                  title="Text color" style={{ width: 34, height: 30, padding: 0, border: '1px solid var(--color-border)', borderRadius: 6 }} />
                <WorkBenchPill value={v.value || 'Preview'} bg={v.color_bg} fg={v.color_text} />
                <button
                  onClick={() => setWbDefault(i)}
                  title={v.is_default ? 'This is the default — click to clear' : 'Make this the default value'}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
                    border: '1px solid ' + (v.is_default ? 'var(--color-primary, #0d9488)' : 'var(--color-border)'),
                    background: v.is_default ? 'var(--color-primary, #0d9488)' : 'transparent',
                    color: v.is_default ? '#fff' : 'var(--color-text-soft)',
                  }}
                >{v.is_default ? '★ Default' : '☆ Default'}</button>
                <button onClick={() => setVals(vs => vs.filter((_, idx) => idx !== i))}
                  title="Remove (retires the choice; existing prescriptions keep it)"
                  style={{ ...linkBtn, color: '#b91c1c' }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {isStatus && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-soft)', cursor: 'pointer' }}>
            <input type="checkbox" checked={mode === 'multi'} disabled={modeSaving}
              onChange={e => switchMode(e.target.checked ? 'multi' : 'single')} />
            Allow multiple selections (checkbox)
          </label>
        )}
        {isStatus && vals.some(v => v.is_default) && (
          <button
            onClick={async () => {
              try {
                // SAVE THE STAR FIRST, ALWAYS.
                //
                // `vals` is local state; the backfill reads the star from the DATABASE. So this
                // button used to act on whatever was last saved, not on what the operator was
                // looking at — and the two are different the moment anyone moves the star and
                // clicks Apply without saving. With no prior default that was a confusing dead end
                // ("No choice is starred as the default for this field"); with a prior default it
                // was worse, and silent: moving the star A -> B and clicking Apply wrote **A** to
                // every empty row across the pharmacy. The dialog's "(Save your star first if you
                // just changed it.)" was a note asking the operator to work around it.
                //
                // Persisting here makes the button mean what it says. It is the same write Save
                // performs, so there is nothing to lose by it, and the preview that follows is then
                // guaranteed to be describing the value on screen.
                await persistValues()
                const p = await api.previewWorkbenchBackfill(def.field_key)
                if (!p.has_default) { alert(p.message); return }
                // The server says WHAT it counted. A workbench choice is usually stored per fill, and
                // telling someone "every prescription already has a value" when the grid is showing
                // them blank refill rows is the kind of wrong answer that sends people to the wrong
                // screen looking for the bug.
                if (p.rows_matched === 0) { alert(`Every ${p.unit === 'prescriptions' ? 'prescription' : 'fill'} already has a value for this field — nothing to fill.`); return }
                if (!confirm(`${p.message}\n\nApply now? Reversible from Backfill history.`)) return
                const r = await api.applyWorkbenchBackfill(def.field_key, p.batch_id)
                alert(r.message)
              } catch (e: any) { setErr(e?.message || 'Backfill failed') }
            }}
            style={linkBtn}
          >Apply default to existing</button>
        )}
        <button onClick={onCancel} style={linkBtn}>Cancel</button>
      </div>
    </div>
  )
}

function FieldEditor({ def, onDone, onCancel }: { def: api.CustomFieldDef | null; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(def?.field_name ?? '')
  const [type, setType] = useState(def?.field_type ?? 'text')
  const [required, setRequired] = useState(def?.is_required ?? false)
  const [options, setOptions] = useState<OptionRow[]>(parseOptions(def?.dropdown_options ?? null))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const addOption = () => setOptions([...options, { label: '', color: null, text: null }])
  const setOption = (i: number, patch: Partial<OptionRow>) => setOptions(options.map((o, idx) => idx === i ? { ...o, ...patch } : o))
  const removeOption = (i: number) => setOptions(options.filter((_, idx) => idx !== i))
  // ASP-82: exactly one default. Clicking the current default clears it (no default).
  const setDefault = (i: number) => setOptions(options.map((o, idx) => ({ ...o, is_default: idx === i ? !o.is_default : false })))

  const save = async () => {
    if (!name.trim()) { setErr('Field name is required'); return }
    setSaving(true); setErr(null)
    try {
      const dropdown_options = type === 'dropdown'
        ? JSON.stringify(options.filter(o => o.label.trim()).map(o => ({ label: o.label.trim(), color: o.color || null, text: o.text || null, is_default: o.is_default === true })))
        : null
      const body = { field_name: name.trim(), field_type: type, dropdown_options, is_required: required }
      if (def) await api.updateCustomFieldDef(def.id, body)
      else await api.createCustomFieldDef(body)
      onDone()
    } catch (e: any) { setErr(e?.message || 'Save failed'); setSaving(false) }
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <h2 style={{ margin: '0 0 12px', fontSize: 20 }}>{def ? 'Edit field' : 'New field'}</h2>
      {err && <div style={errBox}>{err}</div>}
      <label style={lbl}>Field name</label>
      <input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="e.g. Infusion Date, Priority" />
      <label style={lbl}>Type</label>
      <SearchableSelect value={type} onChange={setType} options={FIELD_TYPES} ariaLabel="Field type" />
      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} /> Required
      </label>

      {type === 'dropdown' && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ ...lbl, marginTop: 0 }}>Choices &amp; colors</label>
            <button onClick={addOption} style={linkBtn}><Plus size={14} /> Add choice</button>
          </div>
          {options.length === 0 && <div style={{ fontSize: 13, color: 'var(--color-text-soft)', padding: '8px 0' }}>No choices yet — add some.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <GripVertical size={14} style={{ color: 'var(--color-text-mute)' }} />
                <input value={o.label} onChange={e => setOption(i, { label: e.target.value })} placeholder="Choice label" style={{ ...input, marginBottom: 0, flex: 1 }} />
                <div style={{ display: 'flex', gap: 3 }}>
                  {api.WB_COLOR_TOKENS.map(tok => (
                    <button key={tok.name} title={tok.name}
                      onClick={() => setOption(i, { color: tok.bg, text: tok.text })}
                      style={{ width: 22, height: 22, borderRadius: 6, background: tok.bg,
                        border: o.color === tok.bg ? '2px solid var(--color-text)' : '1px solid var(--color-border)', cursor: 'pointer' }} />
                  ))}
                  <button title="No color" onClick={() => setOption(i, { color: null, text: null })}
                    style={{ width: 22, height: 22, borderRadius: 6, background: '#fff', border: !o.color ? '2px solid var(--color-text)' : '1px solid var(--color-border)', cursor: 'pointer', fontSize: 11 }}>✕</button>
                </div>
                <button
                  onClick={() => setDefault(i)}
                  title={o.is_default ? 'This is the default — click to clear' : 'Make this the default value'}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
                    border: '1px solid ' + (o.is_default ? 'var(--color-primary, #0d9488)' : 'var(--color-border)'),
                    background: o.is_default ? 'var(--color-primary, #0d9488)' : 'transparent',
                    color: o.is_default ? '#fff' : 'var(--color-text-soft)',
                  }}
                >{o.is_default ? '★ Default' : '☆ Default'}</button>
                <div style={{ display: 'none' }}>
                </div>
                {o.label && <WorkBenchPill value={o.label} bg={o.color} fg={o.text} />}
                <button onClick={() => removeOption(i)} style={{ ...linkBtn, color: '#b91c1c' }}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button onClick={save} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : 'Save field'}</button>
        <button onClick={onCancel} style={linkBtn}>Cancel</button>
      </div>
    </div>
  )
}

const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const linkBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, cursor: 'pointer' }
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface)' }
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14, marginBottom: 4, boxSizing: 'border-box' }
const lbl: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginTop: 14, marginBottom: 4 }
const errBox: React.CSSProperties = { padding: '10px 12px', background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 10 }
